// Shared pieces of the Classes and Zones tabs (F-STN-21, F-STN-19; spec/13 §12, §3.4).
//
// A class and a zone each carry a `ColorToken` — a design token that stays visible on every ground,
// never a hex (spec/13 §12, F-ORG-03 rule 5). The picker offers the **categorical palette** — the
// eight equal-weight, meaning-free hues `category-1 … category-8` (#411, ADR-0025, BRAND.md §4.6) —
// as swatches painted with the matching `--clavian-*` variable, so the choice follows a venue's brand.
//
// The semantic tokens (`ok`/`warn`/`danger`), the accent and the greys are **no longer selectable**
// for a class or a zone: they carry a meaning a class colour should not borrow, or read as chrome. A
// class or zone still stored on one of those legacy tokens keeps showing it (pinned after the palette,
// so the current choice is never hidden), but it cannot be chosen anew. `distinctTokens` still folds
// any two swatches a brand override happens to resolve to the same colour, keeping the selected one.

import type { ColorToken } from '@clavian/api-client';
import { CATEGORY_COLOR_TOKENS, COLOR_TOKENS } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { cx, tokenValue } from '@clavian/ui-web';
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/** The inline style that paints a swatch in a colour token, never a hex. */
export function swatchStyle(token: ColorToken): CSSProperties {
  return { backgroundColor: tokenValue(token) };
}

/** The `--clavian-*` value of a colour token, for a border or a dot rather than a fill. */
export function tokenColor(token: ColorToken): string {
  return tokenValue(token);
}

/**
 * The tokens the picker offers (F-ORG-03 rule 5, #411/ADR-0025): the categorical palette only. A
 * class or zone stored on a legacy semantic or grey token keeps showing it — pinned after the palette
 * so the current choice is never hidden — but that token cannot be picked anew.
 */
export function offeredTokens(value: ColorToken): readonly ColorToken[] {
  const palette = CATEGORY_COLOR_TOKENS as readonly ColorToken[];
  return palette.includes(value) ? palette : [...palette, value];
}

/**
 * The tokens to offer: one per resolved colour, keeping the selected token in its colour's place.
 * `resolved` maps a token to the colour the page paints it in; a token it cannot resolve stays.
 */
export function distinctTokens(tokens: readonly ColorToken[], resolved: ReadonlyMap<ColorToken, string>, selected: ColorToken): ColorToken[] {
  const groups = new Map<string, ColorToken[]>();
  const order: string[] = [];
  for (const token of tokens) {
    const value = resolved.get(token);
    const key = value === undefined || value === '' ? `token:${token}` : `value:${value}`;
    const group = groups.get(key);
    if (group === undefined) {
      groups.set(key, [token]);
      order.push(key);
    } else {
      group.push(token);
    }
  }
  return order.map((key) => {
    const group = groups.get(key) ?? [];
    return group.includes(selected) ? selected : (group[0] as ColorToken);
  });
}

/** What each token resolves to under the element `probe` sits in: the theme and any venue brand. */
function useResolvedTokens(): [React.RefObject<HTMLSpanElement | null>, ReadonlyMap<ColorToken, string>] {
  const probe = useRef<HTMLSpanElement>(null);
  const [resolved, setResolved] = useState<ReadonlyMap<ColorToken, string>>(new Map());
  useLayoutEffect(() => {
    const element = probe.current;
    if (element === null || typeof window.getComputedStyle !== 'function') return;
    const next = new Map<ColorToken, string>();
    for (const token of COLOR_TOKENS) {
      element.style.color = tokenValue(token);
      next.set(token, window.getComputedStyle(element).color);
    }
    element.style.color = '';
    // A measurement of the painted theme, read once the element is in the page.
    setResolved(next);
  }, []);
  return [probe, resolved];
}

/**
 * The colour-token picker (spec/13 §12): the contract's tokens as named swatches, one per colour. A
 * radio group — arrow keys move, the name is read with each swatch.
 */
export function ColorTokenField({
  label,
  value,
  onValueChange,
  error,
}: {
  readonly label: string;
  readonly value: ColorToken;
  readonly onValueChange: (value: ColorToken) => void;
  readonly error?: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const [probe, resolved] = useResolvedTokens();
  const tokens = distinctTokens(offeredTokens(value), resolved, value);
  const name = `color-${label.replace(/\W+/g, '-').toLowerCase()}`;

  return (
    <fieldset className="console-swatches" aria-invalid={error === undefined ? undefined : true}>
      <legend className="clav-field__label">{label}</legend>
      <span ref={probe} className="console-swatches__probe" aria-hidden="true" />
      <div className="console-swatches__grid">
        {tokens.map((token) => {
          const checked = token === value;
          return (
            <label key={token} className={cx('console-swatches__option', checked && 'is-checked')}>
              <input
                type="radio"
                className="clav-visually-hidden"
                name={name}
                value={token}
                checked={checked}
                onChange={() => onValueChange(token)}
              />
              <span className="console-swatches__chip" style={swatchStyle(token)} aria-hidden="true" />
              <span>{t(`stations.colorToken.${token}`)}</span>
            </label>
          );
        })}
      </div>
      {error === undefined ? null : <p className="clav-field__error">{error}</p>}
    </fieldset>
  );
}

/**
 * The live tile preview of a class (spec/13 §3.1, §12): the floor tile a station of this class would
 * wear, drawn with the Floor's own tile styles, in session so the brand fill shows. `number` is
 * illustrative; the real number is the station's.
 */
export function ClassTilePreview({
  name,
  colorToken,
  pricingLabel,
  capacity,
  ageLimit,
  number = 12,
}: {
  readonly name: string;
  readonly colorToken: ColorToken;
  readonly pricingLabel: string;
  readonly capacity: number;
  readonly ageLimit: number | null;
  readonly number?: number;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-class-preview">
      <div className="console-tile console-tile--free console-tile--preview" style={{ '--tile-class': tokenColor(colorToken) } as CSSProperties}>
        <span className="console-tile__head">
          <span className="console-tile__number">{number}</span>
          <span className="console-tile__status">
            <span className="console-tile__dot" />
            {t('floor.state.free')}
          </span>
        </span>
        <span className="console-tile__meta">
          <span className="console-tile__class-dot" />
          <span className="console-tile__name">{name === '' ? t('stations.classes.previewName') : name}</span>
        </span>
        <span className="console-tile__foot">
          <span className="console-tile__sub">{t('floor.tile.ready')}</span>
        </span>
      </div>
      <p className="console-subtle">
        {pricingLabel}
        {' · '}
        {t('stations.classes.seats', { count: capacity })}
        {ageLimit === null ? '' : ` · ${t('stations.classes.agePreview', { age: ageLimit })}`}
      </p>
    </div>
  );
}
